import { useEffect, useState } from "react";
import { Link, useNavigate } from "../../lib/i18n";
import { Minus, Plus, X, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Progress } from "../ui/progress";
import { useCart } from "../../context/CartContext";
import { useSite } from "../../context/SiteContext";
import { api, fmt } from "../../lib/api";

export const QtyStepper = ({ value, onChange, testid = "qty" }) => (
  <div className="inline-flex items-center rounded-md border border-beige bg-white">
    <button onClick={() => onChange(value - 1)} className="p-2 text-pierre hover:text-brun" data-testid={`${testid}-minus`} aria-label="Moins"><Minus size={14} /></button>
    <input value={value} onChange={(e) => onChange(parseInt(e.target.value) || 1)} className="w-10 bg-transparent text-center text-sm font-semibold text-brun outline-none" data-testid={`${testid}-input`} />
    <button onClick={() => onChange(value + 1)} className="p-2 text-pierre hover:text-brun" data-testid={`${testid}-plus`} aria-label="Plus"><Plus size={14} /></button>
  </div>
);

export default function MiniCart() {
  const { items, open, setOpen, update, remove, payload, add } = useCart();
  const { delivery, marketing } = useSite();
  const [quote, setQuote] = useState(null);
  const [recos, setRecos] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!open || !items.length) return;
    api.post("/cart/quote", { ...payload, postal_code: null }).then((r) => setQuote(r.data)).catch(() => {});
  }, [open, items]); // eslint-disable-line

  useEffect(() => {
    if (open && marketing?.recommended_skus) api.get("/products", { params: { limit: 40 } }).then((r) => setRecos(r.data.filter((p) => marketing.recommended_skus.includes(p.sku))));
  }, [open, marketing]);

  const sub = quote?.subtotal || 0;
  const thr = delivery?.free_threshold || 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col bg-ivoire p-0 sm:max-w-md" data-testid="mini-cart">
        <div className="border-b border-beige p-6"><SheetTitle className="font-serif text-3xl text-brun">Votre panier</SheetTitle></div>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center p-10 text-center">
            <p className="font-serif text-2xl text-brun">Votre panier est vide</p>
            <p className="mt-2 text-sm text-pierre">Commencez par notre bois sélectionné.</p>
            <button onClick={() => { setOpen(false); nav("/boutique?univers=bois"); }} className="btn-primary mt-6">Commander mon bois</button>
          </div>
        ) : (
          <>
            {thr > 0 && (
              <div className="bg-creme px-6 py-4 text-xs text-encre">
                <p className="mb-2 flex items-center gap-2"><Truck size={14} className="text-bois" />
                  {sub >= thr ? "Livraison standard offerte sur votre commande" : <>Plus que <b>{fmt(thr - sub)}</b> pour la livraison standard offerte</>}</p>
                <Progress value={Math.min(100, (sub / thr) * 100)} className="h-1 bg-beige [&>div]:bg-bois" />
              </div>
            )}
            <div className="flex-1 space-y-5 overflow-y-auto p-6">
              {items.map((it, i) => (
                <div key={i} className="flex gap-4" data-testid={`mini-cart-item-${i}`}>
                  <img src={it.image} alt="" className="h-20 w-20 rounded object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <Link to={`/produit/${it.slug}`} onClick={() => setOpen(false)} className="text-sm font-medium text-brun leading-snug">{quote?.lines?.find((l) => l.product_id === it.product_id)?.name || it.name}</Link>
                      <button onClick={() => remove(i)} className="text-pierre hover:text-brun" data-testid={`mini-cart-remove-${i}`} aria-label="Retirer"><X size={15} /></button>
                    </div>
                    <p className="text-xs text-pierre">{it.option ? `${it.option} · ` : ""}{it.unit}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <QtyStepper value={it.qty} onChange={(v) => update(i, v)} testid={`mini-cart-qty-${i}`} />
                      <span className="font-serif text-lg text-brun">{fmt((quote?.lines?.find((l) => l.product_id === it.product_id && l.option === it.option)?.unit_price ?? it.price) * it.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {recos.length > 0 && (
                <div className="border-t border-beige pt-5">
                  <p className="eyebrow mb-3">Souvent ajoutés</p>
                  {recos.slice(0, 3).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="flex items-center gap-3 text-sm text-brun"><img src={p.images?.[0]} alt="" className="h-10 w-10 rounded object-cover" />{p.name}</span>
                      <button onClick={() => add(p, 1, null, true)} className="text-xs font-semibold uppercase tracking-wider text-bois hover:text-brun" data-testid={`upsell-add-${p.sku}`}>+ {fmt(p.price)}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="border-t border-beige bg-white p-6">
              <div className="flex justify-between text-sm"><span className="text-pierre">Sous-total TTC</span><span className="font-serif text-2xl text-brun" data-testid="mini-cart-subtotal">{fmt(sub)}</span></div>
              <p className="mt-1 text-xs text-pierre">Livraison calculée selon votre code postal.</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button onClick={() => { setOpen(false); nav("/panier"); }} className="btn-ghost !px-3" data-testid="mini-cart-view-cart">Voir le panier</button>
                <button onClick={() => { setOpen(false); nav("/commande"); }} className="btn-primary !px-3" data-testid="mini-cart-checkout">Commander</button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
